import {NgModule} from '@angular/core';
import {RouterModule, Routes, Route, UrlSegment} from '@angular/router';
import {VerifyComponent} from './verify/verify.component';
import {DataPickComponent} from './data-pick/data-pick.component';
import {LoginRegisterComponent} from './login-register/login-register.component';
import {LoginRegisterNewComponent} from './login-register-new/login-register-new.component';
import {DynamicStepsComponent} from './dynamic-steps/dynamic-steps.component';
import {NotificationComponent} from './notification/notification.component';
import { UserComponent } from './user/user.component';
import {NonuserComponent} from './nonuser/nonuser.component';
import {NoInternetComponent} from './no-internet/no-internet.component';
import {AppService} from './app.service';
import { TemplatesComponent  } from './templates/components/templates/templates.component';
import { BackendStatusGuard } from './guards/backend-status/backend-status.guard';
import { WarehouseTypeGuard } from './guards/warehouse-type/warehouse-type.guard';
import { WelcomeEmailComponent } from './welcome-email/welcome-email.component';
import {NS_Enums } from './shared/namespaces';
import { PricingComponent } from './account/components/subscription/child-components/pricing/pricing.component';
import { NewPricingGuard } from './guards/subscription/subscriptions.guard';
import { AuthComponent } from './auth/auth.component';
import {AuthGuard} from './guards/auth/auth.guard';
import { LogoutComponent } from './logout/logout.component';

const baseWord = 'u';
const uncommonBaseWord = '';

// Note: Keep this array in sync with the routes defined in admin-config-routing.module.ts
const ADMIN_CONFIG_PATHS = [
  'managesettings', 'subscription-pricing', 'connectors',
  'add-source-new', 'add-studio-source', 'edit-studio-source', 'clone-studio-source',
  'edit-integration-new', 'studio-edit-attach', 'clone-integration'
];

const routes: Routes = [
  {path: '', redirectTo: '/' + baseWord + '/integrations-list' , pathMatch: 'full'},
  {
    path : uncommonBaseWord, component: NonuserComponent, canActivate: [AuthGuard],
    children: [
      // {path: 'verify-password', component: VerifyComponent},
      {path: 'close', component: DataPickComponent},
      {path: 'logout', component: LogoutComponent},
      // {path: 'login', component: LoginRegisterComponent},
      {path: 'login', component: AuthComponent, data: { title: 'Login Page' }},
      {path: 'auth', component: AuthComponent, data: { title: 'Auth Page' }},
      {path: 'verify', component: WelcomeEmailComponent},
      {path: 'no-internet', component: NoInternetComponent},
      {path: 'error', component: DataPickComponent}
    ]
  },
  {
    path : baseWord, component: UserComponent, canActivateChild: [AppService, BackendStatusGuard, AuthGuard],
    children: [
      {path: '', redirectTo: '/u/integrations-list', pathMatch: 'full', data: { title: 'Sources List Page' }},
      {path: 'dashboard', loadChildren: () => import('./dashboard-new/dashboard.module').then(m => m.DashboardModule)},
      {path: 'dashboard-new', loadChildren: () => import('./dashboard-new/dashboard.module').then(m => m.DashboardModule)},
      // {path: 'pricing', component: SubscriptionsComponent},
      {path: 'pricing', component: PricingComponent, canActivate: [NewPricingGuard], data: { title: 'Pricing Page'}},
      // Route alias for backward compatibility: 'integrations-list' URL maps to SourcesModule
      {path: 'integrations-list', loadChildren: () => import('./sources/sources.module').then(m => m.SourcesModule)},
      {path: 'destinations-list', loadChildren: () => import('./destinations/destinations.module').then(m => m.DestinationsModule)},
      {path: 'integration-details', loadChildren: () => import('./integration-details/integration-details.module').then(m => m.IntegrationDetailsModule)},
      // Route alias for backward compatibility: 'sources' URL maps to IntegrationsListModule
      {path: 'sources', loadChildren: () => import('./integrations-list/integrations-list.module').then(m => m.IntegrationsListModule)},
      {path: 'add-source/:name', component: DynamicStepsComponent},
      {path: 'notifications', component: NotificationComponent, data: { title: 'Notifications Page'}},
      {path: 'ask-daton', loadChildren: () => import('./ask-daton/ask-daton.module').then(m => m.AskDatonModule)},
      // Links by warehouse kind (e.g. from iQ): the guard redirects to warehouse/<id> for that type.
      {path: 'warehouse/type/:type', component: DynamicStepsComponent, canActivate: [WarehouseTypeGuard]},
      {path: 'warehouse/:name', component: DynamicStepsComponent},
      {path: 'edit-warehouse/:name/:warehouseId', component: DynamicStepsComponent, data: { title: 'Edit Warehouse Page'}},
      {path: 'logs', loadChildren: () => import('./job-logs/job-logs.module').then(m => m.JobLogsModule)},
      {path: 'admin', loadChildren: () => import('./admin/admin.module').then(m => m.AdminModule)},
      // Lazy-loaded config group (managesettings, subscription-pricing, connectors, source config).
      // Empty-path child so URLs and the UserComponent shell/guards are preserved. Must stay last.
      {
        path: '',
        canMatch: [(_route: Route, segments: UrlSegment[]) => ADMIN_CONFIG_PATHS.includes(segments[0]?.path)],
        loadChildren: () => import('./admin-config/admin-config.module').then(m => m.AdminConfigModule)
      },
    //  { path: '**', component: IntegrationsListComponent }
    ]
  },
  // tslint:disable-next-line:max-line-length
  { path: 'u/account', loadChildren: () => import('./account/account.module').then(m => m.AccountModule), canActivateChild: [AppService, BackendStatusGuard, AuthGuard] },
  // tslint:disable-next-line:max-line-length
  { path: 'u/templates', loadChildren: () => import('./templates/templates.module').then(m => m.TemplatesModule), canActivateChild: [AppService, BackendStatusGuard, AuthGuard] },
  { path: 'u/studio', loadChildren: () => import('./connector-studio/connector-studio.module').then(m => m.ConnectorStudioModule), canActivateChild: [AppService, BackendStatusGuard, AuthGuard] },
  { path: 'u/alerts', loadChildren: () => import('./modules/alerts/alerts.module').then(m => m.AlertsModule)},
  { path: `u/${NS_Enums.routePathEnum.dashboards}`, loadChildren: () => import('./modules/dashboards/dashboards.module').then(m => m.DashBoardsModule), canActivateChild: [AppService, BackendStatusGuard, AuthGuard] },
  { path: '**', redirectTo: '/' + baseWord  + '/integrations-list'}
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
